import React, { useCallback, useState, useRef, useEffect } from "react";
import { Button, Card, CardContent, Grid, Table, TableBody, TableRow, styled, CircularProgress, Box, Typography } from "@mui/material";

import { ISOXMLCheckResult, ResponseMessage } from "../orm/dto";
import LoginRequired from "./LoginRequired";
import InfoBox from "./Common/InfoBox";
import ISOXMLCheckHelp from "./help/ISOXMLCheckHelp";
import { StyledTableCell } from "./utils";
import { FilePicker } from "./Common/FilePicker";
import { checkISOXMLTaskSet, convertResultISOXMLTaskSet, startISOXMLCheckJob, getISOXMLCheckJobStatus } from "../orm/dev4ag";

const ColoredTableCell = styled(StyledTableCell)<{ cellcolor: string }>`
    background-color: ${props => props.cellcolor};
`;




const ResponseMessageRow: React.FC<{ message: ResponseMessage }> = ({ message }) => {
    let color;
    switch (message.type) {
        case 'Error':
            color = 'red';
            break;
        case 'Warning':
            color = 'orange';
            break;
        case 'Info':
            color = 'green';
            break;
        default:
            color = 'white';
    }

    return (
        <TableRow>
            <ColoredTableCell cellcolor={color}>{message.type}</ColoredTableCell>
            <StyledTableCell>{message.title}</StyledTableCell>
            <StyledTableCell>{message.description}</StyledTableCell>
        </TableRow>
    );
};



export default function ISOXMLCheck() {

    const [isoxmlFile, setISOXMLFile] = useState<File>()
    const [analysisResponse, setAnalysisResponse] = useState<ISOXMLCheckResult | null>(null)
    const [errorMessage, setErrorMessage] = useState<string>()
    const [isActive, setIsActive] = useState<boolean>()
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [loadingMessage, setLoadingMessage] = useState<string>('')
    const abortControllerRef = useRef<AbortController | null>(null)

    // Cleanup effect to abort any pending requests when component unmounts
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    const onCheck = useCallback(async () => {
        // Prevent multiple simultaneous requests
        if (isActive || isLoading) {
            return;
        }

        setIsActive(true);
        setIsLoading(true);
        setLoadingMessage('Starting ISOXML check job...');
        setAnalysisResponse(null);
        setErrorMessage('');

        try {
            // Start the check job
            const jobResult = await startISOXMLCheckJob({
                ISOXMLFile: isoxmlFile,
                Config: {
                    CheckProprietaryElements: false
                }
            });

            if ('message' in jobResult) {
                setErrorMessage(jobResult.message);
                setIsActive(false);
                setIsLoading(false);
                setLoadingMessage('');
                return;
            }

            // Poll for completion
            let attempts = 0;
            const maxAttempts = 150; // 5 minutes with 2-second intervals
            
            while (attempts < maxAttempts) {
                setLoadingMessage(`Checking ISOXML... (${attempts + 1}/${maxAttempts})`);
                
                const statusResult = await getISOXMLCheckJobStatus(jobResult.jobId);
                
                if ('message' in statusResult) {
                    setErrorMessage(statusResult.message);
                    setIsActive(false);
                    setIsLoading(false);
                    setLoadingMessage('');
                    return;
                }

                const { status, result, errorMessage } = statusResult;
                
                if (status === 'Completed') {
                    if (result && result.messages) {
                        setAnalysisResponse(new ISOXMLCheckResult(result.messages));
                    }
                    setLoadingMessage('Check completed!');
                    setTimeout(() => {
                        setIsActive(false);
                        setIsLoading(false);
                        setLoadingMessage('');
                    }, 1000);
                    return;
                } else if (status === 'Failed') {
                    setErrorMessage(errorMessage || 'Check job failed');
                    setIsActive(false);
                    setIsLoading(false);
                    setLoadingMessage('');
                    return;
                }
                
                // Wait 2 seconds before next poll
                await new Promise(resolve => setTimeout(resolve, 2000));
                attempts++;
            }
            
            setErrorMessage('Check job timed out after 5 minutes');
            setIsActive(false);
            setIsLoading(false);
            setLoadingMessage('');

        } catch (error) {
            console.error('Error during ISOXML check:', error);
            setErrorMessage('An error occurred during checking');
            setIsActive(false);
            setIsLoading(false);
            setLoadingMessage('');
        }
    }, [isoxmlFile, isActive, isLoading])



    const onConvert = useCallback(async () => {
        // Prevent multiple simultaneous requests
        if (isActive || isLoading) {
            return;
        }

        setIsActive(true);
        setIsLoading(true);
        setLoadingMessage('Processing ISOXML and preparing download...');
        setAnalysisResponse(null);
        setErrorMessage('');

        // Create new AbortController for this request
        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        // Set a timeout to prevent hanging requests (5 minutes)
        const timeoutId = setTimeout(() => {
            console.log('Request timeout reached, aborting...');
            abortController.abort();
        }, 5 * 60 * 1000);

        try {
            // Check if the signal is already aborted before making the request
            if (abortController.signal.aborted) {
                throw new Error('Request was cancelled before starting');
            }

            const error = await convertResultISOXMLTaskSet({
                ISOXMLFile: isoxmlFile,            
                Config: {
                    CheckProprietaryElements: true
                }
            }, abortController.signal);
            
            if (error && error.messages && error.messages.length > 0) {
                setErrorMessage(error.messages[0].title);
            }
        } catch (err) {
            if (err instanceof Error && err.name === 'AbortError') {
                setErrorMessage('Operation cancelled by user');
            } else if (err instanceof Error && err.message.includes('cancelled')) {
                setErrorMessage('Operation cancelled by user');
            } else {
                console.error('Error during ISOXML conversion:', err);
                setErrorMessage('An error occurred during processing');
            }
        } finally {
            clearTimeout(timeoutId);
            setIsActive(false);
            setIsLoading(false);
            setLoadingMessage('');
            abortControllerRef.current = null;
        }
    }, [isoxmlFile, isActive, isLoading])

    const onCancel = useCallback(() => {
        if (abortControllerRef.current) {
            console.log('Aborting request...');
            abortControllerRef.current.abort();
        }
    }, [])

    function LoadingSpinner() {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2 }}>
                <CircularProgress size={40} sx={{ color: 'var(--bitrate-green)', mb: 2 }} />
                <Typography variant="body2" sx={{ mb: 2, textAlign: 'center' }}>
                    {loadingMessage}
                </Typography>
                <Button 
                    onClick={onCancel}
                    variant="outlined"
                    sx={{ 
                        color: 'var(--bitrate-green)', 
                        borderColor: 'var(--bitrate-green)',
                        '&:hover': { 
                            borderColor: '#13995d',
                            backgroundColor: 'rgba(19, 153, 93, 0.04)'
                        }
                    }}
                >
                    Cancel
                </Button>
            </Box>
        );
    }

    function ResultDisplay(){
        if( !analysisResponse){
            return <Table><TableBody><TableRow><StyledTableCell>Select a File and click on Analyse</StyledTableCell></TableRow></TableBody></Table>
        }
        if( analysisResponse.isValid){
            return (
                <Table><TableBody><TableRow><StyledTableCell>This TaskSet is Valid!</StyledTableCell></TableRow></TableBody></Table>
            )
        } else {
            return (
                    <Table>
                        <TableBody>
                            <TableRow>
                                <StyledTableCell>Infos: {analysisResponse.infoCount}</StyledTableCell>
                                <StyledTableCell>Warnings: {analysisResponse.warningCount}</StyledTableCell>
                                <StyledTableCell>Error: {analysisResponse.errorCount}</StyledTableCell>
                            </TableRow> 
                            <TableRow>
                                        <StyledTableCell>Type</StyledTableCell>
                                        <StyledTableCell>Title</StyledTableCell>
                                        <StyledTableCell>Description</StyledTableCell>
                            </TableRow>
                            {analysisResponse?.messages.map((message, index) => (<ResponseMessageRow key={index} message={message} />))}
                        </TableBody>                            
                    </Table>
            )
        }
    }


    return (
        <LoginRequired>
            <Grid container wrap="nowrap" spacing={2} height={1}>
                <Grid item width='530px' sm='auto' container direction="column" spacing={2}>
                    <Grid item>
                        <Card variant="outlined">
                            <CardContent>
                                <Table size="small">
                                    <TableBody>
                                        <TableRow>
                                            <StyledTableCell>ZipFolder with ISOXML TaskSet</StyledTableCell>
                                            <StyledTableCell>
                                                <FilePicker onChange={(file) => setISOXMLFile(file)} />
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                <Button onClick={onCheck} disabled={isActive || isLoading} sx={{ background: 'var(--bitrate-green)', color: 'var(--grey-3)', '&:hover': { background: '#13995d' } }}>
                                                    {isLoading ? 'Checking...' : 'Check'}
                                                </Button>
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                <Button onClick={onConvert} disabled={isActive || isLoading} sx={{ background: 'var(--bitrate-green)', color: 'var(--grey-3)', '&:hover': { background: '#13995d' } }}>
                                                    {isLoading ? 'Processing...' : 'Check And Download'}
                                                </Button>
                                            </StyledTableCell>
                                        </TableRow>
                                        { isLoading &&
                                            <TableRow>
                                                <StyledTableCell colSpan={4}>
                                                    <LoadingSpinner />
                                                </StyledTableCell>
                                            </TableRow>
                                        }
                                    </TableBody>
                                </Table>
                                {errorMessage && (
                                    <Box sx={{ mt: 2, p: 2, backgroundColor: '#ffebee', borderRadius: 1, border: '1px solid #f44336' }}>
                                        <Typography variant="body2" color="error">
                                            {errorMessage}
                                        </Typography>
                                    </Box>
                                )}
                                {ResultDisplay()}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
                <InfoBox>
                    <ISOXMLCheckHelp/>
                </InfoBox>            
            </Grid>
       </LoginRequired>
    )
}